import type { DateTime } from "luxon";
import { parseDelayHours } from "./agencyPostDelay";
import type { AgencySource, NurseRole, OpenShiftRequest } from "./models";

/** Keep persisted single-agency requests editable until their target rows are backfilled. */
export function hasLegacyAgencyPosting(
	request: Pick<OpenShiftRequest, "agency_targets" | "agency_source_name" | "agency_shift_ids" | "agency_post_delay_hours">,
): boolean {
	return (
		(request.agency_targets === undefined || request.agency_targets.length === 0) &&
		(Boolean(request.agency_source_name) ||
			request.agency_post_delay_hours != null ||
			(request.agency_shift_ids !== undefined && request.agency_shift_ids.length > 0))
	);
}

export interface AgencyTargetSelection {
	agency_source: AgencySource;
	role: NurseRole;
	lead_time_hours: number | null;
}

export interface AgencyTargetView extends AgencyTargetSelection {
	origin: "manager" | "automation" | "agency";
	state: "pending" | "posted" | "failed" | "closed";
	scheduled_post_at: DateTime | null;
	filled_count: number;
	open_count: number;
	has_applicants: boolean;
}

export interface AgencyPostingRule {
	agency_source: AgencySource;
	open_shift_auto_post_enabled: boolean;
	call_off_auto_post_enabled: boolean;
	lead_time_hours: number | null;
}

export interface AgencySelectionDraft {
	agency_source: AgencySource;
	role: NurseRole | undefined;
	timing: "now" | "before";
	hours: string;
}

/** Invalid choices stay visible in the editor and block submission, never silently drop an agency. */
export function resolveAgencySelections(drafts: AgencySelectionDraft[]): AgencyTargetSelection[] | null {
	const selections: AgencyTargetSelection[] = [];
	for (const draft of drafts) {
		const leadTime = draft.timing === "now" ? null : parseDelayHours(draft.hours);
		if (!draft.role || (draft.timing === "before" && leadTime === null)) return null;
		if (selections.some((selection) => selection.agency_source === draft.agency_source)) return null;
		selections.push({ agency_source: draft.agency_source, role: draft.role, lead_time_hours: leadTime });
	}
	return selections;
}

export function agencySelectionsMatch(first: AgencyTargetSelection[], second: AgencyTargetSelection[]): boolean {
	return (
		first.length === second.length &&
		first.every((target) =>
			second.some((other) => other.agency_source === target.agency_source && other.role === target.role && other.lead_time_hours === target.lead_time_hours),
		)
	);
}
